/**
 * Normalisation and validation for client and vehicle input.
 * The patterns mirror firestore.rules so users see errors before Firestore refuses a write.
 */
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { todayIso } from './dates';

export const LIMITS = { name: 50, email: 254, make: 40, model: 40 } as const;

export const EMAIL_RE = /^[^@ ]+@[^@ ]+[.][^@ ]+$/;
export const PHONE_RE = /^[+][1-9][0-9]{6,14}$/;
export const PLATE_RE = /^[A-Z0-9][A-Z0-9 -]{0,10}[A-Z0-9]$/;
export const ISO_DATE_RE = /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

export interface ClientInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/** What is stored in a client document, apart from the audit fields. */
export interface ClientFields extends ClientInput {
  lastNameLower: string;
}

/** The parts whose last service is tracked per vehicle. firestore.rules lists the same keys. */
export const PARTS = [
  { key: 'engineOil', label: 'Engine oil and filter' },
  { key: 'brakePads', label: 'Brake pads' },
  { key: 'tyres', label: 'Tires' },
  { key: 'battery', label: 'Battery' },
  { key: 'airFilter', label: 'Air filter' },
] as const;

export type PartKey = (typeof PARTS)[number]['key'];

export const PART_ACTIONS = ['replaced', 'serviced'] as const;
export type PartAction = (typeof PART_ACTIONS)[number];

/** The last time a part was replaced or serviced. */
export interface PartRecord {
  action: PartAction;
  date: string; // YYYY-MM-DD
}

export type PartsRecord = Partial<Record<PartKey, PartRecord>>;

/** Form state for one part: an empty action means "not recorded". */
export interface PartInput {
  action: PartAction | '';
  date: string;
}

export interface VehicleInput {
  plate: string;
  make: string;
  model: string;
  licenseRenewalDate: string;
  parts?: Partial<Record<PartKey, PartInput>>;
}

/** What is stored in a vehicle document, apart from the audit fields. */
export interface VehicleFields {
  plate: string;
  make: string;
  model: string;
  licenseRenewalDate: string;
  parts: PartsRecord;
}

export type FieldErrors<T> = Partial<Record<keyof T, string>>;

export type VehicleErrors = FieldErrors<Omit<VehicleInput, 'parts'>> & {
  parts?: Partial<Record<PartKey, string>>;
};

export type Validated<V, I> = { ok: true; value: V } | { ok: false; errors: FieldErrors<I> };

export function collapseSpaces(s: string): string {
  return s.trim().replace(/\s+/g, ' ');
}

/** Returns the number in E.164 form (+36301234567), or null when it is not a valid number. */
export function normalizePhone(raw: string, defaultCountry: CountryCode): string | null {
  const parsed = parsePhoneNumberFromString(raw.trim(), defaultCountry);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number;
}

/** Upper-case, single spaces, no spaces around hyphens: "aa bc - 123" -> "AA BC-123". */
export function normalizePlate(raw: string): string {
  return collapseSpaces(raw.toUpperCase()).replace(/\s*-\s*/g, '-');
}

/** A real calendar date in YYYY-MM-DD form (rejects 2027-02-30). */
export function isValidIsoDate(s: string): boolean {
  if (!ISO_DATE_RE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

export function validateClient(
  input: ClientInput,
  defaultCountry: CountryCode,
): Validated<ClientFields, ClientInput> {
  const errors: FieldErrors<ClientInput> = {};

  const firstName = collapseSpaces(input.firstName);
  if (!firstName) errors.firstName = 'Enter a first name.';
  else if (firstName.length > LIMITS.name) errors.firstName = `At most ${LIMITS.name} characters.`;

  const lastName = collapseSpaces(input.lastName);
  if (!lastName) errors.lastName = 'Enter a last name.';
  else if (lastName.length > LIMITS.name) errors.lastName = `At most ${LIMITS.name} characters.`;

  const email = input.email.trim().toLowerCase();
  if (!email) errors.email = 'Enter an email address.';
  else if (email.length > LIMITS.email || !EMAIL_RE.test(email)) {
    errors.email = 'Enter a valid email address, like name@example.com.';
  }

  const phone = normalizePhone(input.phone, defaultCountry);
  if (!input.phone.trim()) errors.phone = 'Enter a phone number.';
  else if (!phone || !PHONE_RE.test(phone)) {
    errors.phone = 'Enter a valid phone number, like +36 30 123 4567.';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: { firstName, lastName, lastNameLower: lastName.toLowerCase(), email, phone: phone! },
  };
}

export function validateVehicle(
  input: VehicleInput,
  today: string = todayIso(),
): { ok: true; value: VehicleFields } | { ok: false; errors: VehicleErrors } {
  const errors: VehicleErrors = {};

  const plate = normalizePlate(input.plate);
  if (!plate) errors.plate = 'Enter the licence plate.';
  else if (!PLATE_RE.test(plate)) {
    errors.plate = '2–12 letters, digits, spaces or hyphens, like AA BC-123.';
  }

  const make = collapseSpaces(input.make);
  if (make.length > LIMITS.make) errors.make = `At most ${LIMITS.make} characters.`;

  const model = collapseSpaces(input.model);
  if (model.length > LIMITS.model) errors.model = `At most ${LIMITS.model} characters.`;

  const licenseRenewalDate = input.licenseRenewalDate.trim();
  if (!licenseRenewalDate) errors.licenseRenewalDate = 'Enter the renewal date.';
  else if (!isValidIsoDate(licenseRenewalDate)) errors.licenseRenewalDate = 'Enter a valid date.';

  // A part is recorded only when both an action and a date are given; neither means "not recorded".
  const parts: PartsRecord = {};
  const partErrors: Partial<Record<PartKey, string>> = {};
  for (const { key } of PARTS) {
    const action = input.parts?.[key]?.action ?? '';
    const date = (input.parts?.[key]?.date ?? '').trim();
    if (!action && !date) continue;
    if (!action) partErrors[key] = 'Choose replaced or serviced.';
    else if (!date) partErrors[key] = 'Enter the date.';
    else if (!isValidIsoDate(date)) partErrors[key] = 'Enter a valid date.';
    else if (date > today) partErrors[key] = 'The date cannot be in the future.';
    else parts[key] = { action, date };
  }
  if (Object.keys(partErrors).length > 0) errors.parts = partErrors;

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, value: { plate, make, model, licenseRenewalDate, parts } };
}
